import { LoggingService } from "../sdServices/loggingSvc";
import { RecoveryManager } from "./recovery-manager";
import { ContextService } from "../sdServices/context/context-service";

export interface RecoverableControlOptions {
  /** Prevents the field from automatically being filled on recovery. Defaults to false. */
  preventAutomaticRecovery?: boolean;
}

export interface FieldWithOptions {
  key: string;
  options: RecoverableControlOptions;
}
/**
 * Mark a field as recoverable so it will be automatically save. Controller must have
 * @recoverableControl.
 * @param target Recoverable control
 * @param key
 */
export function recoverableFieldWithOptions(options?: RecoverableControlOptions) {
  return function (target: any, key: string) {
    let fieldWithOptions: FieldWithOptions = { key: key, options: options || <any>{} };
    if (target.$getRecoverableFields) {
      let recoverableFields = target.$getRecoverableFields();
      recoverableFields.push(fieldWithOptions);
      target.$getRecoverableFields = () => recoverableFields;
    } else {
      target.$getRecoverableFields = () => [fieldWithOptions];
    }
  };
}

export function recoverableField(target: any, key: string) {
  recoverableFieldWithOptions(<any>{})(target, key);
}

/** Date is stringified as just a standard string, which once parsed, returns a string, not a Date obj.
 * Save the type so we can reconstruct the object properly on restore.
 */
class ObjectWithType {
  type: string;
  constructor(public value: any) {
    if (value instanceof Date) {
      this.type = "Date";
    }
  }
}
export class RecoverableBinding {
  /**
   * The unique recovery Id to identify this control for recovery purposes.
   * One time binding. Do not use the | symbol in your IDS.
   */
  recoveryId = "@?";
}

export interface RecoverableControlImpl {
  recoveryId?: string;
  $sdRecoveryOnComplete: (data: any) => void;
  $sdRecoveryIsModelValid: (data: any) => boolean;
  $sdRecoveryPreserialize: (data: any) => any;
  [field: string]: any;
  $getRecoverableFields: () => FieldWithOptions[];
}

export class RecoverableControl {
  private recoveryFields: FieldWithOptions[] = [];
  private recoveryInstance: RecoveryManager;
  private recoveryObject: any = {};
  // @ngInject
  constructor(
    private $scope: ng.IScope,
    private contextService: ContextService,
    private control: RecoverableControlImpl,
    private loggingService: LoggingService
  ) {
    if (control.$getRecoverableFields) {
      this.recoveryFields = control.$getRecoverableFields();
      this.$scope.$evalAsync(() => {
        if (control.recoveryId) {
          this.registerRecovery();
        }
      });
    }
  }

  private registerRecovery() {
    this.recoveryInstance = new RecoveryManager(this.contextService, this.$scope, this.control.recoveryId);
    this.recoveryInstance.whenRecover(this.recover.bind(this));
    this.recoveryFields.forEach((field) => {
      this.watchTargetForChanges(field);
    });
  }

  private recover(data: any) {
    let recoverData = this.convertFieldsWithTypeToObjects(data);
    if (!this.control.$sdRecoveryIsModelValid || this.control.$sdRecoveryIsModelValid(recoverData)) {
      Object.keys(recoverData).forEach((key) => {
        let recoveryFieldWithOptions = this.recoveryFields.find((f) => f.key == key);
        if (recoveryFieldWithOptions && !recoveryFieldWithOptions.options.preventAutomaticRecovery) {
          this.control[key] = recoverData[key];
        }
      });
      this.control.$sdRecoveryOnComplete && this.control.$sdRecoveryOnComplete(recoverData);
    } else {
      this.loggingService.logWarning(
        `Recovery model for control is invalid and unrecoverable. recoveryId: ${this.control.recoveryId}`
      );
    }
  }

  private convertFieldsWithTypeToObjects(data: { [key: string]: ObjectWithType }) {
    let returnObj: any = {};
    Object.keys(data).forEach((key) => {
      if (data[key].type == "Date") {
        returnObj[key] = new Date(data[key].value);
      } else {
        returnObj[key] = data[key].value;
      }
    });
    return returnObj;
  }

  private watchTargetForChanges(field: FieldWithOptions) {
    this.$scope.$watch(
      () => {
        return this.control && this.control[field.key];
      },
      (newValue, oldValue) => {
        let preserializer = this.control.$sdRecoveryPreserialize;
        let objectValue = newValue;
        if (preserializer) {
          objectValue = preserializer.bind(this.control)(field.key, newValue);
        }
        this.recoveryObject[field.key] = new ObjectWithType(objectValue);
        this.recoveryInstance.saveState(this.recoveryObject);
      }
    );
  }
}
